// apps/frontend/src/components/layout/Layout.tsx
// 🎨 LAYOUT ULTRA PREMIUM - VERSION FINALE PARFAITE AVEC TOUT CONNECTÉ

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { analytics } from '../../lib/analytics';
import { behaviorTracking } from '../../lib/behaviorTracking';
import { API_BASE_URL } from '../../config/api';
import {
  Home,
  Search,
  Plus,
  MessageCircle,
  User,
  Package,
  Menu,
  X,
  LogOut,
  Bell,
  Wallet,
  Globe,
  ShieldCheck,
  BarChart3,
  ChevronUp,
  Sparkles,
} from 'lucide-react';
import ShoppingMissionWizard from '../missions/ShoppingMissionWizard';
import { SUPPORTED_LOCALES, LOCALES_INFO } from '@mode-app/shared';
import { useAuth } from '../../hooks/useAuth';
import { useProfilesContext } from '../../contexts/ProfilesContext';
import { useToast } from '../../hooks/useToast';
import NotificationBell from '../notifications/NotificationBell';
import { CartIndicator } from '../cart/CartIndicator';
import WalletBadge from '../wallet/WalletBadge';
import ProfileSwitcher from '../profiles/ProfileSwitcher';
import { useNotifications } from '../../hooks/useNotifications';
import { useCommunityUnreadCount } from '../../hooks/useCommunityUnreadCount';
import PreferencesNotice from '../notice/PreferencesNotice';
import AutoTutorialMount from '../onboarding/AutoTutorialMount';
import LikeWowMoment from '../wow/LikeWowMoment';
import FounderLiveBanner from '../common/FounderLiveBanner';
import FounderLiveNavPill from '../common/FounderLiveNavPill';
import AdminNoticeModal from '../common/AdminNoticeModal';
import CommunityVoiceModal from '../common/CommunityVoiceModal';

interface LayoutProps {
  onShowLogin: () => void;
  onShowRegister: () => void;
  onShowSell: () => void;
}

export const Layout: React.FC<LayoutProps> = ({
  onShowLogin,
  onShowRegister,
  onShowSell,
}) => {
  const { t, i18n } = useTranslation(['common', 'profiles-children']);
  const navigate = useNavigate();
  const { user, isAuthenticated, backendStatus, logout } = useAuth();
  const { showSuccess, showError } = useToast();

  // ✅ CONTEXTE PARTAGÉ (même instance que App et ProductsPage)
  const {
    activeProfile,
    profiles,
    hasProfiles,
    isLoading: profilesLoading,
    switchProfile,
    createProfile,
    selectedProfileIds,
    toggleProfileSelection,
  } = useProfilesContext();

  const { notifications } = useNotifications();
  const unreadMessageCount = notifications.filter(
    n => !n.isRead && n.type === 'MESSAGE'
  ).length;
  const communityUnreadCount = useCommunityUnreadCount();

  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');
  // Routes "focused workflow" : on cache la bottom nav mobile pour ne pas
  // recouvrir le CTA sticky de la page (ex: /seller/import, "Créer X
  // brouillons"). Sinon la nav mobile + extensions adblock (overlay
  // z-index 999999999 au bas-droite) bouffent la zone du bouton.
  // Workflows "plein écran" : on masque la nav du bas et le padding de
  // clearance, pour que la page gère elle-même toute la hauteur.
  // La messagerie en fait partie (zone de saisie en bas, comme Vinted).
  const isFocusedWorkflow =
    location.pathname.startsWith('/seller/import') ||
    location.pathname.startsWith('/messages');

  // Pages acheteur (browse/buy intent) : on y promote "S'inscrire" en CTA
  // primary et on cache "Creer ma boutique" pour les visiteurs non-connectes.
  // Sinon le visiteur arrive via un lien partage et voit en gros une CTA
  // qui le pousse a VENDRE alors qu'il vient pour acheter / decouvrir.
  const isBuyerContext =
    location.pathname.startsWith('/product/') ||
    location.pathname.startsWith('/products') ||
    location.pathname.startsWith('/explorer') ||
    location.pathname.startsWith('/search') ||
    location.pathname.startsWith('/favorites') ||
    (location.pathname.startsWith('/seller/') &&
      !location.pathname.startsWith('/seller/dashboard'));

  // Envoie un event page_view à chaque navigation, anonymes compris.
  // Passe par behaviorTracking → /analytics/event avec session_id, ce qui
  // permet à l'admin /live-users de reconstituer le parcours d'un visiteur
  // (connecté OU anonyme) et sa page courante.
  const lastTrackedPath = useRef<string | null>(null);
  const lastPageViewAt = useRef<number>(Date.now());
  useEffect(() => {
    if (location.pathname === lastTrackedPath.current) return;
    const now = Date.now();
    const prevPageMs =
      lastTrackedPath.current !== null ? now - lastPageViewAt.current : 0;
    const referrer = lastTrackedPath.current || '';
    lastTrackedPath.current = location.pathname;
    lastPageViewAt.current = now;
    void behaviorTracking.pageView({
      ...(referrer && { referrer }),
      ...(prevPageMs > 0 && { prevPageMs }),
    });
  }, [location.pathname]);
  const [searchQuery, setSearchQuery] = useState('');
  const [missionWizardOpen, setMissionWizardOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const searchDesktopInputRef = useRef<HTMLInputElement>(null);
  const searchMobileInputRef = useRef<HTMLInputElement>(null);
  const [suggestions, setSuggestions] = useState<{
    brands: string[];
    products: Array<{
      id: string;
      title: string;
      price: number;
      brand?: string;
      image?: string;
    }>;
  } | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchDesktopRef = useRef<HTMLDivElement>(null);
  const searchMobileRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language || 'fr-FR';
  const currentLocaleInfo =
    LOCALES_INFO[currentLang as keyof typeof LOCALES_INFO] ||
    LOCALES_INFO['fr-FR'];

  // 🔒 VÉRIFICATION ADMIN
  const isAdmin =
    isAuthenticated && user && ['ADMIN', 'SUPER_ADMIN'].includes(user.role);

  // ============================================================================
  // 🎯 HANDLERS ULTRA PREMIUM AVEC NOTIFICATIONS
  // ============================================================================

  const handleLanguageChange = async (locale: string) => {
    analytics.languageChanged(locale);
    try {
      await i18n.changeLanguage(locale);
      setIsLanguageOpen(false);
      const localeInfo = LOCALES_INFO[locale as keyof typeof LOCALES_INFO];
      showSuccess(
        t(
          'common:notifications.languageChanged',
          `Langue changée : ${localeInfo?.name}`
        )
      );
    } catch {
      showError(
        t(
          'common:errors.languageChangeFailed',
          'Erreur lors du changement de langue'
        )
      );
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      analytics.headerSearchSubmitted(searchQuery.trim());
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setShowSuggestions(false);
      setIsMobileMenuOpen(false);
    }
  };

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (val.trim().length < 2) {
      setSuggestions(null);
      setShowSuggestions(false);
      return;
    }
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const [bRes, pRes] = await Promise.all([
          fetch(
            `${API_BASE_URL}/api/v1/brands/search?q=${encodeURIComponent(val.trim())}&limit=4`
          ),
          fetch(
            `${API_BASE_URL}/api/v1/products?search=${encodeURIComponent(val.trim())}&limit=4`
          ),
        ]);
        const bData = await bRes.json();
        const pData = await pRes.json();
        const brands: string[] = bData.data?.brands ?? [];
        const productsRaw: any[] = pData.data?.products ?? pData.products ?? [];
        const products = productsRaw.slice(0, 4).map((p: any) => ({
          id: p.id,
          title: p.product_translations?.[0]?.title ?? p.title ?? 'Article',
          price: p.price,
          brand: p.brand,
          image: p.product_images?.[0]?.url,
        }));
        if (brands.length === 0 && products.length === 0) {
          setSuggestions(null);
          setShowSuggestions(false);
          return;
        }
        setSuggestions({ brands, products });
        setShowSuggestions(true);
      } catch {
        // silently ignore
      }
    }, 250);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        !searchDesktopRef.current?.contains(t) &&
        !searchMobileRef.current?.contains(t)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll-to-top visibility
  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Custom event "glowp:openMission" : permet a n'importe quelle page
  // (notamment FamilyLandingPage) d'ouvrir le mission wizard sans
  // remonter de prop drilling.
  useEffect(() => {
    const onOpenMission = () => setMissionWizardOpen(true);
    window.addEventListener('glowp:openMission', onOpenMission);
    return () => window.removeEventListener('glowp:openMission', onOpenMission);
  }, []);

  // Cmd+K / Ctrl+K → focus search
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        const input =
          window.innerWidth >= 768
            ? searchDesktopInputRef.current
            : searchMobileInputRef.current;
        if (input) {
          input.focus();
          input.select();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const renderSuggestions = () => {
    if (!showSuggestions || !suggestions) return null;
    return (
      <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-[#e8e2d4] shadow-xl z-[100] overflow-hidden">
        {suggestions.brands.length > 0 && (
          <div>
            <div className="px-3 pt-2.5 pb-1 text-[10px] font-semibold text-[#a89280] uppercase tracking-wider">
              Marques
            </div>
            {suggestions.brands.map(brand => (
              <button
                key={brand}
                onMouseDown={() => {
                  navigate(`/products?brand=${encodeURIComponent(brand)}`);
                  setSearchQuery('');
                  setShowSuggestions(false);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[#faf6f0] transition-colors text-left"
              >
                <Search className="w-3.5 h-3.5 text-[#a89280] shrink-0" />
                <span className="text-sm text-brand-dark">{brand}</span>
              </button>
            ))}
          </div>
        )}
        {suggestions.products.length > 0 && (
          <div
            className={
              suggestions.brands.length > 0 ? 'border-t border-[#f0ebe3]' : ''
            }
          >
            <div className="px-3 pt-2.5 pb-1 text-[10px] font-semibold text-[#a89280] uppercase tracking-wider">
              Articles
            </div>
            {suggestions.products.map(product => (
              <button
                key={product.id}
                onMouseDown={() => {
                  navigate(`/product/${product.id}`);
                  setSearchQuery('');
                  setShowSuggestions(false);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[#faf6f0] transition-colors text-left"
              >
                {product.image ? (
                  <img
                    src={product.image}
                    alt=""
                    className="w-8 h-10 object-cover rounded shrink-0"
                  />
                ) : (
                  <div className="w-8 h-10 bg-[#f5f0ea] rounded shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-brand-dark truncate">
                    {product.title}
                  </div>
                  {product.brand && (
                    <div className="text-xs text-[#a89280] truncate">
                      {product.brand}
                    </div>
                  )}
                </div>
                <span className="text-sm font-semibold text-brand-500 shrink-0">
                  {Number(product.price).toFixed(2)} €
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const handleNavigation = (path: string) => {
    analytics.navTabClicked(path);
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-brand-page">
      {/* Notice admin -> user en pop-up. Le composant ne render rien
          quand pas de notice. Z-index 80 => au-dessus de presque tout. */}
      <AdminNoticeModal />

      {/* Community Voice — "Marie-Amelie a une question pour toi".
          Defere de 3s, respecte critical path + preference user. */}
      <CommunityVoiceModal />

      {/* Bandeau Founder Live — visible globalement quand un live est
          a venir dans la semaine ou en cours. Se masque tout seul sinon. */}
      <FounderLiveBanner />

      {/* 🔌 STATUS BACKEND — Visible uniquement pour les admins */}
      {isAdmin && (
        <div
          className={`px-4 py-2 text-center text-sm transition-all duration-300 ${
            backendStatus.connected
              ? 'bg-gradient-to-r from-[#f5ede0] to-[#FEFAF5] text-brand-dark border-b border-brand-border'
              : 'bg-gradient-to-r from-red-50 to-rose-50 text-red-700 border-b border-red-100'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <span className="relative flex h-2 w-2">
              <span
                className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                  backendStatus.connected ? 'bg-brand-accent' : 'bg-red-400'
                }`}
              ></span>
              <span
                className={`relative inline-flex rounded-full h-2 w-2 ${
                  backendStatus.connected ? 'bg-brand-500' : 'bg-red-500'
                }`}
              ></span>
            </span>
            <span className="font-medium">{backendStatus.message}</span>
            <span className="text-xs opacity-75">({backendStatus.url})</span>
          </div>
        </div>
      )}

      {/* 🎨 HEADER ULTRA PREMIUM */}
      <header className="bg-[#1c1917] sticky top-0 z-50 border-b border-[#C8A882]/15">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* 🌟 LOGO PREMIUM AVEC ANIMATION */}
            <button
              onClick={() => handleNavigation('/home')}
              className="flex items-center gap-3 hover:opacity-80 transition-all duration-300 group"
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#C8A882] to-[#B89968] shadow-md group-hover:shadow-lg transition-all duration-300 group-hover:scale-110">
                <span className="text-white font-bold text-sm">G</span>
              </div>
              <h1 className="text-xl font-semibold text-white">GlowP</h1>
            </button>

            {/* 🔍 ZONE DE RECHERCHE CENTRALE - DESKTOP */}
            <div
              ref={searchDesktopRef}
              className="hidden md:flex flex-1 min-w-[240px] max-w-xl mx-4 relative items-center gap-2"
            >
              <form onSubmit={handleSearch} className="relative group flex-1">
                <input
                  ref={searchDesktopInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={e => handleSearchChange(e.target.value)}
                  onFocus={() => {
                    if (suggestions) setShowSuggestions(true);
                  }}
                  placeholder={t('common:buttons.search', 'Rechercher... (⌘K)')}
                  className="w-full px-4 py-2.5 pr-10 border border-white/15 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:border-transparent transition-all duration-300 placeholder-white/40 group-hover:border-white/30"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.10)',
                    color: 'white',
                    WebkitTextFillColor: 'white',
                    caretColor: 'white',
                  }}
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 rounded-lg text-white/65 hover:text-white hover:bg-white/10 transition-all duration-200"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </button>
              </form>

              {renderSuggestions()}
            </div>

            {/* 📱 NAVIGATION CENTRALE - DESKTOP ONLY */}
            <nav className="hidden lg:flex items-center space-x-4 xl:space-x-6">
              <button
                onClick={() => handleNavigation('/home')}
                className="text-white/90 hover:text-white text-sm font-medium transition-all duration-200 relative group"
              >
                {t('common:navigation.home', 'Accueil')}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#C8A882] transition-all duration-300 group-hover:w-full"></span>
              </button>

              <button
                onClick={() => handleNavigation('/products')}
                className="text-white/90 hover:text-white text-sm font-medium transition-all duration-200 relative group"
              >
                {t('common:navigation.shop', 'Catalogue')}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#C8A882] transition-all duration-300 group-hover:w-full"></span>
              </button>

              {/* Mission shopping (multi-articles) : déplacée ici depuis la
                  search zone — bouton icône seul jusqu'en 2xl pour ne pas
                  déborder sur les écrans <1536px. */}
              <button
                type="button"
                onClick={() => {
                  analytics.missionButtonClicked('header_desktop');
                  setMissionWizardOpen(true);
                }}
                title="Chercher plusieurs articles à la fois (rentrée, garde-robe…)"
                aria-label="Chercher plusieurs articles"
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border transition-all hover:bg-white/5"
                style={{
                  borderColor: 'rgba(200,168,130,0.45)',
                  color: '#f0c87a',
                }}
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span className="text-xs font-semibold hidden 2xl:inline whitespace-nowrap">
                  Plusieurs articles
                </span>
              </button>

              {/* 📦 MES COMMANDES - Icône seule (libère la place pour la
                  recherche) — visible uniquement si connecté */}
              {user?.id && isAuthenticated && (
                <button
                  onClick={() => handleNavigation('/orders')}
                  title={t('common:navigation.orders', 'Commandes')}
                  aria-label={t('common:navigation.orders', 'Commandes')}
                  className="flex items-center justify-center w-9 h-9 rounded-lg text-white/90 hover:text-white hover:bg-white/10 transition-all duration-200"
                >
                  <Package className="w-5 h-5" />
                </button>
              )}

              {/* 💎 DASHBOARD PRO - Icône seule — visible uniquement si connecté */}
              {user?.id && isAuthenticated && (
                <button
                  onClick={() => handleNavigation('/account?tab=dashboard')}
                  title={t('common:navigation.dashboard', 'Dashboard')}
                  aria-label={t('common:navigation.dashboard', 'Dashboard')}
                  className="flex items-center justify-center w-9 h-9 rounded-lg text-white/90 hover:text-white hover:bg-white/10 transition-all duration-200"
                >
                  <BarChart3 className="w-5 h-5" />
                </button>
              )}

              <a
                href="#"
                className="text-white/90 hover:text-white text-sm font-medium transition-all duration-200 relative group"
              >
                {t('common:navigation.help', 'Aide')}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#C8A882] transition-all duration-300 group-hover:w-full"></span>
              </a>
            </nav>

            {/* 🎮 ACTIONS DROITE */}
            <div className="flex items-center gap-3">
              {/* 🌍 LANGUAGE SELECTOR */}
              <div className="relative hidden sm:block">
                <button
                  onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200"
                >
                  <span className="text-lg">{currentLocaleInfo?.flag}</span>
                  <span className="hidden 2xl:inline font-medium">
                    {currentLang.split('-')[0].toUpperCase()}
                  </span>
                </button>

                {isLanguageOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsLanguageOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 bg-white border border-brand-border rounded-xl shadow-xl z-50 min-w-48 overflow-hidden animate-fade-in">
                      {SUPPORTED_LOCALES.map(locale => {
                        const localeInfo =
                          LOCALES_INFO[locale as keyof typeof LOCALES_INFO];
                        return (
                          <button
                            key={locale}
                            onClick={() => handleLanguageChange(locale)}
                            className={`w-full text-left px-4 py-3 text-sm hover:bg-brand-500 hover:text-white transition-all duration-200 flex items-center gap-3 ${
                              currentLang === locale ? 'bg-brand-offwhite' : ''
                            }`}
                          >
                            <span className="text-xl">{localeInfo?.flag}</span>
                            <span className="font-medium">
                              {localeInfo?.name}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* 💰 BADGE WALLET - Desktop uniquement si connecté */}
              {isAuthenticated && (
                <div className="hidden lg:block">
                  <WalletBadge
                    onClick={() => {
                      if (activeProfile?.id) {
                        navigate('/account?tab=wallet');
                      }
                    }}
                  />
                </div>
              )}

              {/* 🔐 ADMIN - Icône discrète desktop */}
              {isAdmin && (
                <button
                  onClick={() => handleNavigation('/admin')}
                  title="Dashboard Admin"
                  className="hidden lg:flex items-center justify-center w-9 h-9 rounded-lg text-white/75 hover:text-white hover:bg-white/10 transition-all duration-200"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.75}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                </button>
              )}

              {/* Pastille LIVE — uniquement si un Founder Live est en cours.
                  Discrete par defaut (le composant ne render rien sinon). */}
              <FounderLiveNavPill />

              {/* 🛒 PANIER GLOBAL - Desktop si connecte. Dispatch un event
                  custom 'glowp:openCart' que App.tsx ecoute pour ouvrir le
                  CartModal. Permet de retrouver son panier depuis n'importe
                  quelle page (avant : visible uniquement sur fiche produit). */}
              {isAuthenticated && (
                <div className="hidden lg:block">
                  <CartIndicator
                    onClick={() =>
                      window.dispatchEvent(new CustomEvent('glowp:openCart'))
                    }
                    size="md"
                  />
                </div>
              )}

              {/* 🔔 CLOCHE NOTIFICATIONS - Desktop uniquement si connecté */}
              {isAuthenticated && (
                <div className="hidden lg:block">
                  <NotificationBell />
                </div>
              )}

              {/* 👤 PROFILE SWITCHER OU BOUTONS LOGIN - DESKTOP */}
              <div className="hidden lg:flex items-center gap-3">
                {user?.id && isAuthenticated ? (
                  <ProfileSwitcher
                    profiles={profiles as any}
                    activeProfile={activeProfile as any}
                    hasProfiles={hasProfiles}
                    isLoading={profilesLoading}
                    onSwitchProfile={switchProfile}
                    onCreateProfile={createProfile}
                    selectedProfileIds={selectedProfileIds}
                    onToggleSelection={toggleProfileSelection}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={onShowLogin}
                      className="text-sm text-white/90 hover:text-white font-medium px-4 py-2 rounded-xl hover:bg-white/10 transition-all duration-200"
                    >
                      {t('common:navigation.login', 'Se connecter')}
                    </button>
                    <button
                      onClick={onShowRegister}
                      className={
                        isBuyerContext
                          ? // Pages acheteur : S'inscrire devient le CTA primary (gold gradient).
                            'px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-300 hover:opacity-90 hover:scale-105 bg-gradient-to-r from-[#C8A882] to-[#B89968] text-white shadow-md hover:shadow-lg'
                          : // Pages neutres / seller : S'inscrire reste secondary outlined.
                            'text-sm px-4 py-2 rounded-xl border-2 font-medium transition-all duration-300 hover:scale-105 border-[#C8A882] text-[#C8A882] hover:bg-[#C8A882] hover:text-[#0f0d0b] shadow-sm'
                      }
                    >
                      {t('common:navigation.signup', "S'inscrire")}
                    </button>
                  </div>
                )}

                {/* "Creer ma boutique" : visible pour les users connectes partout
                    et pour les visiteurs non-connectes UNIQUEMENT en dehors des
                    pages acheteur (sur product/shop, le CTA primary devient
                    S'inscrire pour ne pas pousser le visiteur a vendre). */}
                {(isAuthenticated || !isBuyerContext) && (
                  <button
                    onClick={onShowSell}
                    className="px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-300 hover:opacity-90 hover:scale-105 bg-gradient-to-r from-[#C8A882] to-[#B89968] text-white shadow-md hover:shadow-lg"
                  >
                    {t('common:nav.sellButton', 'Commencer à vendre')}
                  </button>
                )}
              </div>

              {/* 📱 BOUTON HAMBURGER — MOBILE/TABLET */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden flex items-center justify-center w-10 h-10 rounded-xl text-white hover:bg-white/10 transition-all duration-200"
                aria-label="Menu"
              >
                {isMobileMenuOpen ? (
                  <X className="w-5 h-5" />
                ) : (
                  <Menu className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* 📱 MENU MOBILE DÉROULANT */}
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div
              className="lg:hidden fixed inset-0 bg-black/20 z-40"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <div
              className="lg:hidden absolute left-0 right-0 top-full z-50 border-t shadow-xl max-h-[calc(100vh-4rem)] overflow-y-auto pb-20"
              style={{
                background: 'linear-gradient(to bottom, #221f1b, #1c1917)',
                borderColor: 'rgba(255,255,255,0.08)',
              }}
            >
              <div className="px-4 py-4 space-y-1">
                {/* ── PROFILS (même composant que desktop) ── */}
                {user?.id && isAuthenticated && (
                  <div className="mb-3">
                    <ProfileSwitcher
                      profiles={profiles as any}
                      activeProfile={activeProfile as any}
                      hasProfiles={hasProfiles}
                      isLoading={profilesLoading}
                      onSwitchProfile={switchProfile}
                      onCreateProfile={createProfile}
                      selectedProfileIds={selectedProfileIds}
                      onToggleSelection={toggleProfileSelection}
                    />
                  </div>
                )}

                {/* ── NAVIGATION ── */}
                <button
                  onClick={() => handleNavigation('/home')}
                  className="w-full text-left px-4 py-3 text-white/80 hover:bg-white/8 rounded-xl transition-all duration-200 font-medium flex items-center gap-3"
                >
                  <Home className="w-5 h-5 text-[#C8A882]" />
                  {t('common:navigation.home', 'Accueil')}
                </button>

                <button
                  onClick={() => handleNavigation('/products')}
                  className="w-full text-left px-4 py-3 text-white/80 hover:bg-white/8 rounded-xl transition-all duration-200 font-medium flex items-center gap-3"
                >
                  <Search className="w-5 h-5 text-[#C8A882]" />
                  {t('common:navigation.shop', 'Catalogue')}
                </button>

                {user?.id && isAuthenticated && (
                  <>
                    <button
                      onClick={() => handleNavigation('/orders')}
                      className="w-full text-left px-4 py-3 text-white/80 hover:bg-white/8 rounded-xl transition-all duration-200 font-medium flex items-center gap-3"
                    >
                      <Package className="w-5 h-5 text-[#C8A882]" />
                      {t('common:navigation.orders', 'Mes commandes')}
                    </button>

                    <button
                      onClick={() => handleNavigation('/messages')}
                      className="w-full text-left px-4 py-3 text-white/80 hover:bg-white/8 rounded-xl transition-all duration-200 font-medium flex items-center gap-3"
                    >
                      <MessageCircle className="w-5 h-5 text-[#C8A882]" />
                      {t('common:navigation.messages', 'Messages')}
                    </button>

                    <button
                      onClick={() => handleNavigation('/community')}
                      className="w-full text-left px-4 py-3 text-white/80 hover:bg-white/8 rounded-xl transition-all duration-200 font-medium flex items-center gap-3"
                    >
                      <Sparkles className="w-5 h-5 text-[#C8A882]" />
                      <span className="flex-1">
                        {t('common:navigation.community', 'Communauté')}
                      </span>
                      {communityUnreadCount > 0 && (
                        <span className="min-w-[20px] h-5 rounded-full bg-[#C8A882] text-white text-[10px] font-bold flex items-center justify-center px-1.5 leading-none">
                          {communityUnreadCount >= 99
                            ? '99+'
                            : communityUnreadCount}
                        </span>
                      )}
                    </button>

                    <button
                      onClick={() => handleNavigation('/notifications')}
                      className="w-full text-left px-4 py-3 text-white/80 hover:bg-white/8 rounded-xl transition-all duration-200 font-medium flex items-center gap-3"
                    >
                      <Bell className="w-5 h-5 text-[#C8A882]" />
                      {t('common:navigation.notifications', 'Notifications')}
                    </button>

                    <button
                      onClick={() => handleNavigation('/account?tab=wallet')}
                      className="w-full text-left px-4 py-3 text-white/80 hover:bg-white/8 rounded-xl transition-all duration-200 font-medium flex items-center gap-3"
                    >
                      <Wallet className="w-5 h-5 text-[#C8A882]" />
                      {t('common:navigation.wallet', 'Porte-monnaie')}
                    </button>

                    <button
                      onClick={() => handleNavigation('/account?tab=dashboard')}
                      className="w-full text-left px-4 py-3 text-white/80 hover:bg-white/8 rounded-xl transition-all duration-200 font-medium flex items-center gap-3"
                    >
                      <BarChart3 className="w-5 h-5 text-[#C8A882]" />
                      {t('common:navigation.dashboard', 'Dashboard')}
                    </button>
                  </>
                )}

                {isAdmin && (
                  <button
                    onClick={() => handleNavigation('/admin')}
                    className="w-full text-left px-4 py-3 text-white/80 hover:bg-white/8 rounded-xl transition-all duration-200 font-medium flex items-center gap-3"
                  >
                    <ShieldCheck className="w-5 h-5 text-[#C8A882]" />
                    {t('common:navigation.admin', 'Admin')}
                  </button>
                )}

                {/* ── SÉPARATEUR ── */}
                <div className="py-2">
                  <div className="border-t border-white/10" />
                </div>

                {/* ── LANGUE — dropdown compact ── */}
                <div className="px-4 py-2">
                  <p className="text-xs font-medium text-white/45 mb-2 flex items-center gap-2">
                    <Globe className="w-3.5 h-3.5" />
                    {t('common:navigation.language', 'Langue')}
                  </p>
                  <div className="relative">
                    <select
                      value={currentLang}
                      onChange={e => handleLanguageChange(e.target.value)}
                      className="w-full appearance-none px-4 py-2.5 pr-10 border border-white/15 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:border-transparent transition-all"
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.10)',
                        color: 'white',
                        WebkitTextFillColor: 'white',
                      }}
                    >
                      {SUPPORTED_LOCALES.map(locale => {
                        const localeInfo =
                          LOCALES_INFO[locale as keyof typeof LOCALES_INFO];
                        return (
                          <option key={locale} value={locale}>
                            {localeInfo?.flag} {localeInfo?.name}
                          </option>
                        );
                      })}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg
                        className="w-4 h-4 text-brand-warm"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* ── SÉPARATEUR ── */}
                <div className="py-2">
                  <div className="border-t border-white/10" />
                </div>

                {/* ── ACTIONS ── */}
                {user?.id && isAuthenticated ? (
                  <>
                    <button
                      onClick={() => {
                        onShowSell();
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full px-4 py-3 bg-gradient-to-r from-[#C8A882] to-[#B89968] text-white rounded-xl transition-all duration-200 font-semibold shadow-md hover:opacity-90 flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      {t('common:nav.sellButton', 'Commencer à vendre')}
                    </button>

                    <button
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        logout();
                      }}
                      className="w-full text-left px-4 py-3 text-red-400 hover:bg-red-500/10 rounded-xl transition-all duration-200 font-medium flex items-center gap-3"
                    >
                      <LogOut className="w-5 h-5" />
                      {t('common:navigation.logout', 'Se déconnecter')}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        onShowLogin();
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full px-4 py-3 text-[#C8A882] font-medium rounded-xl border-2 border-[#C8A882] hover:bg-[#C8A882] hover:text-[#0f0d0b] transition-all duration-200"
                    >
                      {t('common:navigation.login', 'Se connecter')}
                    </button>
                    <button
                      onClick={() => {
                        onShowRegister();
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full px-4 py-3 bg-gradient-to-r from-[#C8A882] to-[#B89968] text-white rounded-xl transition-all duration-200 font-medium shadow-md hover:opacity-90"
                    >
                      {t('common:navigation.signup', "S'inscrire")}
                    </button>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </header>

      {/* 🔍 ZONE DE RECHERCHE MOBILE
          La form de recherche est masquee sur /home + / (le hero a sa propre
          search). En revanche le bouton "Plusieurs articles" reste visible
          partout — sinon l'utilisatrice apprend a le trouver dans le header
          puis le perd des qu'elle revient sur la home. */}
      {(() => {
        const hideSearchForm =
          location.pathname === '/home' || location.pathname === '/';
        return (
          <div
            ref={searchMobileRef}
            className="md:hidden px-4 py-3 bg-[#1c1917] border-b border-[#C8A882]/15 relative flex items-center gap-2"
          >
            <form
              onSubmit={handleSearch}
              className={`relative group flex-1 ${hideSearchForm ? 'hidden' : ''}`}
            >
              <input
                ref={searchMobileInputRef}
                type="text"
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                onFocus={() => {
                  if (suggestions) setShowSuggestions(true);
                }}
                placeholder={t('common:buttons.search', 'Rechercher...')}
                className="w-full px-4 py-2.5 pr-10 border border-white/15 placeholder-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:border-transparent transition-all duration-300"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.10)',
                  color: 'white',
                  WebkitTextFillColor: 'white',
                  caretColor: 'white',
                }}
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-all duration-200"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </button>
            </form>

            {/* Bouton "Plusieurs articles" — toujours visible mobile (meme sur
            la home ou la search form est cachee). Quand la search form est
            visible a cote, on garde un bouton compact icon-only ; quand
            elle est cachee, le bouton prend toute la largeur avec le label
            pour ne pas se retrouver tout seul a droite avec rien. */}
            <button
              type="button"
              onClick={() => {
                analytics.missionButtonClicked('header_mobile');
                setMissionWizardOpen(true);
              }}
              aria-label="Chercher plusieurs articles"
              className={`flex items-center justify-center gap-2 rounded-xl border transition-all ${
                hideSearchForm ? 'flex-1 px-4 py-2.5' : 'flex-shrink-0 p-2.5'
              }`}
              style={{
                borderColor: 'rgba(200,168,130,0.5)',
                backgroundColor: 'rgba(200,168,130,0.12)',
                color: '#f0c87a',
              }}
            >
              <Sparkles className="w-4 h-4" />
              {hideSearchForm && (
                <span className="text-sm font-semibold">
                  Chercher plusieurs articles
                </span>
              )}
            </button>

            {renderSuggestions()}
          </div>
        );
      })()}

      {/* Wizard de mission shopping, accessible globalement depuis le header */}
      <ShoppingMissionWizard
        isOpen={missionWizardOpen}
        onClose={() => setMissionWizardOpen(false)}
      />

      {/* 📄 CONTENU DES PAGES */}
      <main
        className={`min-h-[calc(100vh-200px)] ${isAdminPage || isFocusedWorkflow ? 'pb-0' : 'pb-20 md:pb-0'}`}
      >
        <AnimatePresence mode="wait">
          {/* Route transition — aligné avec constants/motion.ts
              (duration.base = 300ms, ease.out = cubic-bezier(0.16,1,0.3,1)).
              Souffle calme, pas snappy : Glowp arrive, ne saute pas. */}
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* 📱 BOTTOM NAV MOBILE — comme Vinted ──────────────────────────────── */}
      {!isAdminPage && !isFocusedWorkflow && (
        <nav
          className="md:hidden fixed bottom-0 left-0 right-0 z-50 border-t backdrop-blur-xl"
          style={{
            background: 'rgba(254, 250, 245, 0.96)',
            borderColor: 'var(--brand-border)',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
        >
          <div className="flex items-center justify-around h-16 px-2">
            {/* Accueil */}
            <button
              onClick={() => handleNavigation('/home')}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-colors ${
                location.pathname === '/home' || location.pathname === '/'
                  ? 'text-brand-500'
                  : 'text-brand-muted'
              }`}
            >
              <Home
                className="w-5 h-5"
                strokeWidth={location.pathname === '/home' ? 2.5 : 1.8}
              />
              <span className="text-[10px] font-medium">
                {t('common:navigation.home', 'Accueil')}
              </span>
            </button>

            {/* Catalogue */}
            <button
              onClick={() => handleNavigation('/products')}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-colors ${
                location.pathname === '/products' ||
                location.pathname.startsWith('/product')
                  ? 'text-brand-500'
                  : 'text-brand-muted'
              }`}
            >
              <Search
                className="w-5 h-5"
                strokeWidth={
                  location.pathname === '/products' ||
                  location.pathname.startsWith('/product')
                    ? 2.5
                    : 1.8
                }
              />
              <span className="text-[10px] font-medium">
                {t('common:navigation.shop', 'Catalogue')}
              </span>
            </button>

            {/* Vendre — bouton central proéminent */}
            <button
              onClick={onShowSell}
              className="flex flex-col items-center gap-0.5 -mt-5"
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#C8A882] to-[#B89968] flex items-center justify-center shadow-lg shadow-[#C8A882]/30 active:scale-95 transition-transform">
                <Plus className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <span className="text-[10px] font-medium text-brand-500 mt-0.5">
                {t('common:nav.sellButton', 'Vendre')}
              </span>
            </button>

            {/* Messages */}
            <button
              onClick={() => handleNavigation('/messages')}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-colors ${
                location.pathname.startsWith('/messages')
                  ? 'text-brand-500'
                  : 'text-brand-muted'
              }`}
            >
              <div className="relative">
                <MessageCircle
                  className="w-5 h-5"
                  strokeWidth={
                    location.pathname.startsWith('/messages') ? 2.5 : 1.8
                  }
                />
                {unreadMessageCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 rounded-full bg-brand-500 text-white text-[9px] font-bold flex items-center justify-center px-0.5 leading-none shadow-sm">
                    {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                  </span>
                )}
              </div>
              <span className="text-[10px] font-medium">
                {t('common:navigation.chat', 'Messages')}
              </span>
            </button>

            {/* Profil */}
            <button
              onClick={() => {
                if (isAuthenticated) {
                  handleNavigation('/account');
                } else {
                  onShowLogin();
                }
              }}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-colors ${
                location.pathname === '/account'
                  ? 'text-brand-500'
                  : 'text-brand-muted'
              }`}
            >
              {isAuthenticated && activeProfile?.personal_info?.avatar_url ? (
                <img
                  src={activeProfile.personal_info.avatar_url}
                  alt=""
                  className={`w-5 h-5 rounded-full object-cover ring-2 ${
                    location.pathname === '/account'
                      ? 'ring-[#C8A882]'
                      : 'ring-transparent'
                  }`}
                />
              ) : (
                <User
                  className="w-5 h-5"
                  strokeWidth={location.pathname === '/account' ? 2.5 : 1.8}
                />
              )}
              <span className="text-[10px] font-medium">
                {isAuthenticated
                  ? t('common:navigation.profile', 'Profil')
                  : t('common:navigation.login', 'Connexion')}
              </span>
            </button>
          </div>
        </nav>
      )}

      {/* Scroll-to-top floating button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed right-4 z-40 w-10 h-10 rounded-full bg-white border border-brand-border shadow-lg flex items-center justify-center hover:bg-brand-offwhite hover:shadow-xl transition-all duration-200 active:scale-95"
            style={{ bottom: 'calc(env(safe-area-inset-bottom) + 4.5rem)' }}
            aria-label="Retour en haut"
          >
            <ChevronUp className="w-5 h-5 text-brand-dark" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* 🦶 FOOTER COMPACT — masqué sur l'admin (qui gère son propre layout
          full-screen avec sidebar + main scrollable) pour éviter que le
          footer Glowp grignote la zone des actions admin et cache des boutons. */}
      {isAdminPage ? null : (
        <footer className="bg-gradient-to-br from-gray-900 to-gray-800 border-t border-[#2d2a26] py-5 mt-auto">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              {/* Logo + copyright */}
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#C8A882] to-[#B89968]">
                  <span className="text-white font-bold text-xs">G</span>
                </div>
                <span className="text-sm font-semibold text-white">GlowP</span>
                <span className="text-xs text-brand-warm ml-1">
                  © {new Date().getFullYear()}
                </span>
              </div>

              {/* Links */}
              <div className="flex items-center flex-wrap justify-center gap-x-4 gap-y-1">
                <button
                  onClick={() => navigate('/cgu')}
                  className="text-xs transition-colors hover:text-brand-500"
                  style={{ color: '#6b5a4e' }}
                >
                  CGU
                </button>
                <span style={{ color: '#3d3028' }}>·</span>
                <button
                  onClick={() => navigate('/privacy')}
                  className="text-xs transition-colors hover:text-brand-500"
                  style={{ color: '#6b5a4e' }}
                >
                  Confidentialité
                </button>
                <span style={{ color: '#3d3028' }}>·</span>
                <button
                  onClick={() => navigate('/mentions-legales')}
                  className="text-xs transition-colors hover:text-brand-500"
                  style={{ color: '#6b5a4e' }}
                >
                  Mentions légales
                </button>
                <span style={{ color: '#3d3028' }}>·</span>
                <button
                  onClick={() =>
                    window.dispatchEvent(
                      new Event('glowp:open-cookie-settings')
                    )
                  }
                  className="text-xs transition-colors hover:text-brand-500"
                  style={{ color: '#6b5a4e' }}
                >
                  Cookies
                </button>
                <span style={{ color: '#3d3028' }}>·</span>
                <a
                  href="mailto:hello@glowp.app"
                  className="text-xs transition-colors hover:text-brand-500"
                  style={{ color: '#6b5a4e' }}
                >
                  Contact
                </a>
              </div>
            </div>
          </div>
        </footer>
      )}

      <PreferencesNotice />
      <AutoTutorialMount />
      <LikeWowMoment />
    </div>
  );
};

export default Layout;
